// imports
import { Badge, Box, Button, Typography, useTheme } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// components
import { Alert } from 'components/common/Alert';
import DataLoader from 'components/common/DataLoader';
import NoDataFound from 'components/common/NoDataFound';
import { PlanDialog } from 'components/common/PlanDialog';
import { CancelSubscription } from './CancelSubscription';

// helpers, constants, interface
import {
  ADD_SUBSCRIPTION_TEXT,
  CANCEL_PLAN_TEXT,
  EXPIRES_ON_TEXT,
  FROM_TEXT,
  UPGRADE_PLAN_TEXT,
  YOUR_PLAN_TEXT,
} from 'constants/constant';

import { HTTP_STATUS, STAFF_ROUTE, USERS_ROUTE } from 'constants/index';
import { useAuthContext } from 'contexts/AuthContext';
import { CheckoutContext } from 'contexts/CheckoutContext';
import { UserSubscriptionsContext } from 'contexts/UserSubscriptions';
import {
  StripeIntervalType,
  Subscription,
  SubscriptionPayload,
  SubscriptionStatus,
  useGetOrganizationSubscriptionLazyQuery,
  useGetOrgStaffSubscriptionLazyQuery,
  useGetStaffSubscriptionLazyQuery,
} from 'generated/graphql';
import { isOrgProp, ModeType } from 'interfaces';
import { formatTimeStamp, handleLogout, isAdmin, isSuperAdmin } from 'lib/helper';
import { ActionType } from 'reducer/checkoutReducer';
import { DarkGreyCardBox } from 'theme/styleComponent';
import { flexCenterJustifyBetween, flexCenterJustifyEnd } from 'theme/styleConstant';

const CurrentPlan: FC<isOrgProp> = ({ isOrg, isOrgStaff }) => {
  // hooks
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();

  // contexts
  const { dispatch } = useContext(CheckoutContext);
  const { userRoles } = useAuthContext();
  const { userSubscription } = useContext(UserSubscriptionsContext);

  // states
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionPayload['subscription']>(null);
  const [isLoading, setIsLoading] = useState(false);

  // const
  const admin = isAdmin(userRoles);
  const supperAdmin = isSuperAdmin(userRoles);
  const { id: userId, staffId } = params;

  const [getStaffSubscription] = useGetStaffSubscriptionLazyQuery({
    onCompleted: (data) => {
      const { getStaffSubscription } = data || {};
      const { subscription, response } = getStaffSubscription || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setSubscription(subscription as Subscription);
        setIsLoading(false);
      } else {
        Alert.error(message ?? '');
      }
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const [getOrgStaffSubscription] = useGetOrgStaffSubscriptionLazyQuery({
    onCompleted: (data) => {
      const { getOrgStaffSubscription } = data || {};
      const { subscription, response } = getOrgStaffSubscription || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setSubscription(subscription as Subscription);
        setIsLoading(false);
      } else {
        Alert.error(message ?? '');
      }
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const [getOrgSubscription, { loading: fetchLoading, error }] = useGetOrganizationSubscriptionLazyQuery({
    onCompleted: (data) => {
      const { getOrganizationSubscription } = data || {};
      const { subscription, response } = getOrganizationSubscription || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setSubscription(subscription as Subscription);
        setIsLoading(false);
      } else {
        Alert.error(message ?? '');
      }
    },
    onError: () => {
      setIsLoading(false);
    },
  });
  const noData = Boolean((!fetchLoading && !subscription) || error);

  const {
    interval,
    pricingId,
    trialStart,
    trialEnd,
    currentPeriodStart,
    currentPeriodEnd,
    status,
    cancelAt,
    cancelAtPeriodEnd,
    id: subscriptionId,
  } = subscription || {};
  const startDate = formatTimeStamp(currentPeriodStart || '');
  const endDate = formatTimeStamp(currentPeriodEnd || '');
  const trialStartDate = formatTimeStamp(trialStart || '');
  const trialEndDate = formatTimeStamp(trialEnd || '');
  const cancelDate = formatTimeStamp(cancelAt || '');
  const dateColor = cancelAtPeriodEnd
    ? theme.palette.customRedColor.main
    : theme.palette.customGrayColor.light;

  const handleModalClose = () => {
    dispatch({ type: ActionType.RESET });
  };

  const handleSubscriptionCanceled = () => {
    if (userId) {
      if (isOrg) {
        navigate(USERS_ROUTE);
      } else {
        navigate(STAFF_ROUTE);
      }
    } else {
      handleLogout();
    }
  };

  const handleDialogClose = () => {
    setOpenCancelDialog(false);
  };

  const fetchSubscriptionData = useCallback(async () => {
    setIsLoading(true);
    if (userId) {
      if (isOrgStaff && staffId) {
        // super admin viewing an org staff: userId is the organization id on this route
        await getOrgStaffSubscription({
          variables: {
            getOrgStaffSubscriptionInput: {
              userId: staffId,
              organizationId: userId,
            },
          },
        });
      } else if (isOrg) {
        await getOrgSubscription({
          variables: {
            getSubscriptionByOrgIdInput: {
              organizationId: userId as string,
            },
          },
        });
      } else {
        await getStaffSubscription({
          variables: {
            getSubscriptionInput: {
              userId: userId as string,
            },
          },
        });
      }
    } else {
      setSubscription(userSubscription);
      setIsLoading(false);
    }
  }, [
    userId,
    staffId,
    isOrg,
    isOrgStaff,
    getOrgSubscription,
    getOrgStaffSubscription,
    getStaffSubscription,
    userSubscription,
    setSubscription,
  ]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const renderSubscriptionInfo = () => {
    if (subscription?.id) {
      return (
        <Box minHeight={100}>
          <Box {...flexCenterJustifyBetween} mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              {YOUR_PLAN_TEXT}
            </Typography>
            {admin && !cancelAtPeriodEnd && (
              <Box {...flexCenterJustifyEnd} columnGap={2}>
                {
                  <Button variant="text" onClick={() => setOpenCancelDialog(true)}>
                    {CANCEL_PLAN_TEXT}
                  </Button>
                }
                {interval === StripeIntervalType.Month && (
                  <Button
                    variant="contained"
                    onClick={() => dispatch({ type: ActionType.SET_OPEN, open: true })}>
                    {UPGRADE_PLAN_TEXT}
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Badge color={'success'} badgeContent={status} />
          <Typography variant="h6" fontWeight={600} mt={1}>
            {`${interval}LY`}
          </Typography>
          <Typography variant="subtitle2" fontWeight={400} color={dateColor} mt={1}>
            {cancelAtPeriodEnd
              ? `${EXPIRES_ON_TEXT} ${cancelDate}`
              : status === SubscriptionStatus.Active
              ? `${FROM_TEXT} ${startDate} to ${endDate}`
              : `${FROM_TEXT} ${trialStartDate} to ${trialEndDate}`}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <NoDataFound noData={noData} title="No Active Subscription" />
        {!supperAdmin && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="outlined" onClick={() => dispatch({ type: ActionType.SET_OPEN, open: true })}>
              {ADD_SUBSCRIPTION_TEXT}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <DarkGreyCardBox mt={4}>
      <CancelSubscription
        onCanceled={handleSubscriptionCanceled}
        subscriptionId={subscriptionId as string}
        open={openCancelDialog}
        handleClose={handleDialogClose}
      />

      <PlanDialog
        onClose={handleModalClose}
        currentPlanId={pricingId}
        mode={subscription ? ModeType.UPGRADE : ModeType.ACTIVATE_STAFF}
      />

      {isLoading ? <DataLoader columns={12} rows={3} /> : renderSubscriptionInfo()}
    </DarkGreyCardBox>
  );
};

export default CurrentPlan;
